/**
 * Выгрузка операций в CSV — свои данные человек забирает в таблицу.
 *
 * Бесплатно всегда (`export` в `neverPaid`): данные принадлежат человеку, и
 * без выгрузки годы учёта заперты в одном приложении.
 *
 * Формат под русский Excel: разделитель «;», дробная часть через запятую и
 * BOM в начале — без него Excel читает кириллицу как кракозябры. Google
 * Таблицы и Numbers этот файл тоже открывают.
 */
import type { AppState, Tx } from './store.js';

const PART: Record<string, string> = { need: 'Повседневные нужды', want: 'Свободные траты' };

const HEADER = ['Дата', 'Время', 'Тип', 'Сумма', 'Счёт', 'Счёт зачисления', 'Категория', 'Часть плана', 'Заметка'];

/**
 * Расход — минусом, остальное — плюсом: колонку суммы можно сложить.
 * Корректировка уже со знаком: минус — денег на счёте оказалось меньше.
 */
function signed(t: Tx): number {
  return t.type === 'expense' ? -t.amount : t.amount;
}

function kindOf(t: Tx): string {
  if (t.type === 'expense') return 'Расход';
  if (t.type === 'transfer') return 'Перевод';
  if (t.type === 'adjustment') return 'Корректировка';
  return t.link?.kind === 'income' && t.link.incomeKind === 'refund' ? 'Возврат' : 'Поступление';
}

/** 123456 копеек → «1234,56»: без пробелов между разрядами, иначе Excel не узнает число. */
function amountText(kop: number): string {
  const sign = kop < 0 ? '-' : '';
  const abs = Math.abs(kop);
  return `${sign}${Math.floor(abs / 100)},${String(abs % 100).padStart(2, '0')}`;
}

/** Поле в кавычках, если в нём разделитель, кавычка или перенос строки. */
function cell(v: string): string {
  return /[;"\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** Категория с родителями: «Транспорт / Такси». */
function categoryPath(state: AppState, id: string | undefined): string {
  const names: string[] = [];
  let c = state.categories.find((x) => x.id === id);
  for (let guard = 0; c && guard < 5; guard += 1) {
    names.unshift(c.name);
    const parent = c.parentId;
    c = parent ? state.categories.find((x) => x.id === parent) : undefined;
  }
  return names.join(' / ');
}

export function operationsCsv(state: AppState): string {
  const account = (id?: string) => (id ? state.accounts.find((a) => a.id === id)?.name ?? '' : '');
  const rows = [...state.transactions]
    .sort((a, b) => a.occurredDate.localeCompare(b.occurredDate) || a.occurredAt.localeCompare(b.occurredAt))
    .map((t) => [
      t.occurredDate.split('-').reverse().join('.'),
      t.occurredAt.slice(11, 16),
      kindOf(t),
      amountText(signed(t)),
      account(t.accountId),
      t.type === 'transfer' ? account(t.counterAccountId) : '',
      t.type === 'transfer' || t.type === 'adjustment' ? '' : categoryPath(state, t.categoryId),
      PART[t.part ?? ''] ?? '',
      t.note ?? '',
    ]);
  return '﻿' + [HEADER, ...rows].map((r) => r.map(cell).join(';')).join('\r\n') + '\r\n';
}

/**
 * Отдать файл: на телефоне — через «Поделиться» (сохранить в Файлы, отправить
 * себе), на компьютере — обычной загрузкой.
 */
export async function downloadOperationsCsv(state: AppState): Promise<void> {
  const name = `skoka-operacii-${state.today}.csv`;
  const blob = new Blob([operationsCsv(state)], { type: 'text/csv;charset=utf-8' });
  const file = typeof File === 'function' ? new File([blob], name, { type: blob.type }) : null;
  const touch = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  if (touch && file && navigator.canShare?.({ files: [file] })) {
    try { await navigator.share({ files: [file], title: 'Операции SKOKA' }); return; }
    catch (e) {
      // Человек закрыл «Поделиться» — это ответ, а не ошибка.
      if (e instanceof DOMException && e.name === 'AbortError') return;
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
